import { useState } from 'react';

import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';

interface ICancelMigrationModalProps {
  destinationEnvironment: string;
  loading?: boolean;
  /** The server's own sentence when it refuses, shown here rather than lost on close. */
  refusal?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * The source cannot tell whether the destination has registered these devices yet: the two
 * environments never talk, and the receipt only reaches the source when someone uploads it. So
 * the operator supplies that fact rather than the system guessing it. The checkbox exists to make
 * it a deliberate assertion instead of a default-OK button someone clicks through.
 */
const CancelMigrationModal = ({
  destinationEnvironment,
  loading = false,
  refusal,
  onCancel,
  onConfirm
}: ICancelMigrationModalProps) => {
  const { formatMessage } = useIntl();
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Modal show backdrop="static" data-test="cancel-migration-modal">
      <Modal.Header closeButton onHide={onCancel}>
        <Modal.Title>{formatMessage({ id: 'migrationCancelTitleKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          {formatMessage(
            { id: 'migrationCancelIntroKey' },
            { destination: destinationEnvironment }
          )}
        </p>
        <Alert variant="warning" data-test="cancel-migration-unknown">
          {formatMessage(
            { id: 'migrationCancelUnknownKey' },
            { destination: destinationEnvironment }
          )}
        </Alert>
        {refusal && (
          <Alert variant="danger" data-test="cancel-migration-refused">
            {refusal}
          </Alert>
        )}
        <Form.Check
          id="cancel-migration-confirm"
          data-test="cancel-migration-checkbox"
          checked={confirmed}
          onChange={() => setConfirmed(!confirmed)}
          label={formatMessage({ id: 'migrationCancelAssertKey' })}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onCancel} data-test="cancel-migration-back">
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={!confirmed || loading}
          data-test="cancel-migration-confirm"
        >
          {formatMessage({ id: 'migrationCancelKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CancelMigrationModal;
